import { FC, useMemo } from 'react';

import { GiftDeliveryForAdmin } from '@shared/api/giftDelivery/giftDelivery.interface';
import {
  ThanksPrintTemplate,
} from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { Image } from '@shared/api/media/media.interface';

import { GiftThanksPrintPreview } from '../../giftStore/containers/GiftThanksPrintSection/GiftThanksPrintPreview';

interface Props {
  delivery: GiftDeliveryForAdmin | null;
  templates: ThanksPrintTemplate<Image>[];
  onClose: () => void;
}

// 감사카드 미리보기 + 인쇄 (주문 시 작성한 메시지/보내는분을 템플릿 위에 얹어 표시)
export const GiftBrandThanksCardModal: FC<Props> = function GiftBrandThanksCardModal({
  delivery,
  templates,
  onClose,
}) {
  const tp = delivery?.thanksPrint;

  // 주문에 기록된 templateId로 브랜드 템플릿 레이아웃 매칭
  const template = useMemo(() => {
    if (!tp?.templateId) return undefined;
    return templates.find((t) => t._id === tp.templateId);
  }, [templates, tp?.templateId]);

  if (!delivery || !tp) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-base font-bold">감사카드 인쇄</h3>
          <button
            onClick={onClose}
            aria-label="닫기"
            className="rounded p-1 text-gray-400 hover:bg-gray-100"
          >
            ✕
          </button>
        </div>

        <div className="overflow-y-auto p-4">
          {template ? (
            <div id="thanks-card-print" className="mx-auto w-full">
              <GiftThanksPrintPreview
                template={template}
                message={tp.message}
                sender={tp.senderName}
              />
            </div>
          ) : (
            <div className="rounded-lg bg-gray-50 p-6 text-center text-sm text-gray-500">
              <p>감사카드 템플릿을 찾을 수 없습니다.</p>
              <div className="mt-3 space-y-1 text-left text-gray-700">
                <p>
                  <span className="font-semibold">메시지</span>
                  <br />
                  <span className="whitespace-pre-wrap">{tp.message || '-'}</span>
                </p>
                <p className="pt-2">
                  <span className="font-semibold">보내는 분</span>
                  <br />
                  {tp.senderName || '-'}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2 border-t px-4 py-3">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
          >
            닫기
          </button>
          <button
            onClick={() => window.print()}
            disabled={!template}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            인쇄하기
          </button>
        </div>
      </div>

      {/* 인쇄 시 감사카드만 출력 */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #thanks-card-print, #thanks-card-print * { visibility: visible !important; }
          #thanks-card-print {
            position: fixed !important;
            top: 0; left: 0; right: 0;
            margin: 0 auto !important;
            width: 100% !important;
            max-width: 600px !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          @page { margin: 12mm; }
        }
      `}</style>
    </div>
  );
};
